"use strict";
/**
 * Resolve the Polymarket US sport slug implied by the primary market the
 * operator is currently trading, so the market-selection modals can default to
 * that league instead of making the operator re-pick it.
 *
 * Venue-aware, because the sport is carried differently per venue:
 *   - Kalshi primary: `state.selectedEventTicker` IS the Kalshi event ticker
 *     (`KXMLBGAME-26AUG14STLCHC`), which embeds the series ticker as its
 *     prefix — set atomically with `selectedMarket` in
 *     services/market-selection-service.ts, so the two never drift.
 *     Deliberately NOT `selectedMarket.category`: that field is Kalshi's
 *     broad content taxonomy ("Sports"/"Politics"/…), identical for every
 *     league, not a league identifier — see markets/pm-us-sport-slug.ts.
 *   - PM-US primary: the market itself only says "pm-us", but its cached parent
 *     event carries a `seriesSlug` (`mlb-2026`).
 *
 * Returns `undefined` whenever the league can't be determined (no primary
 * market, a legacy-Polymarket primary, an uncached event, or a league with no
 * PM-US equivalent), so callers fall back to the full sport list.
 *
 * The actual string→slug derivation lives in the pure markets/pm-us-sport-slug
 * module; this file only wires it to app state.
 *
 * @module handlers/modals/shared/pm-us-default-league
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolvePrimaryMarketPmUsSlug = resolvePrimaryMarketPmUsSlug;
exports.resolvePrimaryMarketKalshiLeaguePrefix = resolvePrimaryMarketKalshiLeaguePrefix;
const state_1 = require("../../../state");
const cache_1 = require("../../../markets/cache");
const pm_us_sport_slug_1 = require("../../../markets/pm-us-sport-slug");
/**
 * The PM-US sport slug for the current primary market, or `undefined`.
 * The result is a candidate — callers should confirm it against the live
 * PM-US sports list before skipping the sport picker.
 */
function resolvePrimaryMarketPmUsSlug() {
    const state = (0, state_1.getState)();
    if (!state.selectedMarket)
        return undefined;
    if (state.dataSource === "kalshi") {
        return (0, pm_us_sport_slug_1.kalshiTickerToPmUsSlug)(state.selectedEventTicker ?? undefined);
    }
    if (state.dataSource === "pm-us") {
        const eventId = state.selectedEventTicker;
        if (!eventId)
            return undefined;
        const event = (0, cache_1.getPolymarketUsEventCache)().find((e) => String(e.id ?? e.slug ?? "") === eventId);
        return (0, pm_us_sport_slug_1.pmUsSeriesSlugToSportSlug)(event?.seriesSlug);
    }
    return undefined;
}
/**
 * The Kalshi league prefix (`"KXMLB"`) implied by the current primary market,
 * regardless of the primary's venue — so the step-2 Kalshi series presets can
 * default to that league. A Kalshi primary resolves directly from its event
 * ticker; a PM-US primary resolves via its sport slug (reusing
 * {@link resolvePrimaryMarketPmUsSlug}) mapped back to the Kalshi side.
 *
 * Returns `undefined` when the league can't be determined (no primary, a
 * primary on a venue with no league mapping — legacy Polymarket, Novig — an
 * uncached PM-US event, or a sport Kalshi doesn't list), so callers fall back
 * to the sport-agnostic preset set.
 */
function resolvePrimaryMarketKalshiLeaguePrefix() {
    const state = (0, state_1.getState)();
    if (!state.selectedMarket)
        return undefined;
    if (state.dataSource === "kalshi") {
        return (0, pm_us_sport_slug_1.kalshiTickerToLeaguePrefix)(state.selectedEventTicker ?? undefined);
    }
    if (state.dataSource === "pm-us") {
        return (0, pm_us_sport_slug_1.pmUsSlugToKalshiLeaguePrefix)(resolvePrimaryMarketPmUsSlug());
    }
    return undefined;
}
